import type { IncidentForm, IncidentCategory } from '../types/incident'
import { checkpoint } from '../infrastructure/debug/AppDebugLog'

const NUMBERS: Record<string, string> = {
  jeden: '1', jednego: '1', pierwszy: '1', pierwszego: '1', pierwszej: '1', pierwsza: '1',
  dwa: '2', drugi: '2', drugiego: '2', drugiej: '2', druga: '2',
  trzy: '3', trzeci: '3', trzeciego: '3', trzeciej: '3', trzecia: '3',
  cztery: '4', czwarty: '4', czwartego: '4', czwartej: '4', czwarta: '4', czterech: '4',
  pięć: '5', piąty: '5', piątego: '5', piątej: '5', piąta: '5',
  sześć: '6', szósty: '6', szóstego: '6', szóstej: '6', szósta: '6',
  siedem: '7', siódmy: '7', siódmego: '7', siódmej: '7', siódma: '7',
  osiem: '8', ósmy: '8', ósmego: '8', ósmej: '8', ósma: '8',
  dziewięć: '9', dziewiąty: '9', dziewiątego: '9', dziewiątej: '9', dziewiąta: '9',
  dziesięć: '10', dziesiąty: '10', dziesiątego: '10', dziesiątej: '10', dziesiąta: '10',
  '1': '1', '2': '2', '3': '3', '4': '4', '5': '5',
  '6': '6', '7': '7', '8': '8', '9': '9', '10': '10',
}

// Potoczne/emocjonalne określenia awarii, jakich mogą użyć zdenerwowani brygadziści
// na hali. Świadomie BEZ wulgaryzmów wymierzonych w ludzi lub obraźliwych ogólnie —
// takie treści i tak zablokowałby filtr antymobbingowy (guard_filter.ts) wcześniej,
// więc dodawanie ich tutaj byłoby bez sensu. Te słowa służą WYŁĄCZNIE jako wyzwalacze
// rozpoznania — nigdy nie trafiają do finalnego tekstu (pole "typ"/"kategoria" jest
// zawsze mapowane na czystą, profesjonalną wartość, np. "awaria").
export const COLLOQUIAL_FAILURE_WORDS = [
  'cholera', 'kurna', 'psiakrew', 'szlag',
  'kicha', 'syf', 'masakra',
  'padło', 'zdechło', 'wysiadło', 'wykorkowało', 'kipnęło', 'wyzionęło',
  'rozwaliło się', 'rozjechało się', 'sypie się',
  'poszło z dymem', 'strzeliło', 'walnęło', 'huknęło',
  'nawaliło', 'zawiodło', 'odmówiło posłuszeństwa',
  'zatkało się', 'zapchało się', 'zaklinowało się',
  'do kitu', 'kaput', 'ubite', 'zjechane',
]

// Uwaga: rdzenie słów (np. "awari" zamiast "awaria"), żeby łapać odmiany
// przez przypadki ("awarię", "awarii", "awarią") — dopasowanie po pełnym
// słowie w mianowniku pomijało bardzo częste, naturalne sformułowania mówione.
const INCIDENT_WORDS = [
  'wyciek', 'wycieka', 'cieknie', 'wyleciek', 'olej', 'smar', 'płyn', 'chłodziwo',
  'ślisko', 'poślizg', 'poślizgnąć', 'upadek', 'upadł', 'awari',
  'zagroż', 'niebezpiecz', 'uszkodz', 'pęknięcie', 'pęknięty', 'pęknię',
  ...COLLOQUIAL_FAILURE_WORDS,
]

// Uwaga: "linia"/"linii" są jednocześnie typem LOKALIZACJI (locMatch niżej) — jeśli
// zostały już wykorzystane jako lokalizacja, są wykluczane z kandydatów na obiekt,
// żeby "linia 3" nie trafiało jednocześnie w obydwa pola (patrz logika niżej).
const KNOWN_OBJECTS = ['cnc', 'frezarka', 'frezarki', 'tokarka', 'tokarki', 'obrabiarka', 'obrabiarki', 'prabiarka', 'prabiarki', 'prasa', 'prasie', 'wyciskarka', 'spawarka', 'suszarka', 'podnośnik', 'dźwig', 'przenośnik', 'taśma', 'agregat', 'silnik', 'wentylator', 'klimatyzacja', 'ogrzewanie', 'komputer', 'monitor', 'wózek', 'regał', 'pompa', 'wentylacja', 'kompressor', 'wąż', 'wężyk', 'rura', 'wanna', 'wanny', 'wannie', 'linia', 'linii', 'stoisko', 'stoiska', 'stoisku', 'podajnik', 'podajnika', 'urządzenie', 'urządzenia', 'maszyna', 'maszyny', 'zgrzewarka', 'zgrzewarki',
  // Dodane po realnych testach — brygadziści używają tych słów bardzo często,
  // a ich brak w słowniku powodował całkowitą utratę pola "obiekt".
  'pakowarka', 'pakowarki', 'pakowaczka', 'pakowaczki',
  'ładowarka', 'ładowarki',
  'paczkomat', 'paczkomatu', 'paczkomacie',
  'dysza', 'dyszy',
  'sprzęgło', 'sprzęgła', 'sprzęgiełko',
]

// Słowa ogólne (nie konkretne nazwy maszyn) — gdy w tej samej wypowiedzi trafi się
// i słowo ogólne, i konkretna nazwa maszyny, wygrywa ta konkretna (np. "zgrzewarka",
// nie "maszyna", gdy oba padły w tym samym zdaniu).
const GENERIC_OBJECT_WORDS = new Set(['urządzenie', 'urządzenia', 'maszyna', 'maszyny', 'linia', 'linii'])

const OBJECT_BASE: Record<string, string> = {
  obrabiarki: 'obrabiarka',
  frezarki: 'frezarka',
  tokarki: 'tokarka',
  prabiarka: 'obrabiarka',
  prabiarki: 'obrabiarka',
  prasie: 'prasa',
  wanny: 'wanna',
  wannie: 'wanna',
  linii: 'linia',
  stoiska: 'stoisko',
  stoisku: 'stoisko',
  podajnika: 'podajnik',
  urządzenia: 'urządzenie',
  maszyny: 'maszyna',
  zgrzewarki: 'zgrzewarka',
  pakowarki: 'pakowarka',
  pakowaczki: 'pakowaczka',
  ładowarki: 'ładowarka',
  paczkomatu: 'paczkomat',
  paczkomacie: 'paczkomat',
  dyszy: 'dysza',
  sprzęgła: 'sprzęgło',
  sprzęgiełko: 'sprzęgło',
}

// Kolejność priorytetowa: BHP zawsze wygrywa (bezpieczeństwo), potem Awaria, na końcu Magazyn.
// Zredukowane z 5 do 3 kategorii — "Jakość" i "Serwis" (dawne kategorie) wpadają do
// "Awaria" (wada jakościowa/przegląd/naprawa to w praktyce problem ze sprzętem lub
// procesem), a "Braki" wpada do "Magazyn".
const CATEGORY_KEYWORDS: [IncidentCategory, string[]][] = [
  ['BHP', [
    'wypadek', 'poślizg', 'poślizgnął', 'poślizgnęła', 'upadek', 'upadł', 'upadła',
    'oparzenie', 'ewakuacja', 'pożar', 'ppoż',
    'kwas', 'kwasu', 'kwasem', 'żrąca', 'żrący', 'żrące', 'opary', 'trujące', 'skażenie',
    'uraz', 'ranny', 'ranna', 'krew', 'plama kwasu', 'poparzenie', 'porażenie',
  ]],
  ['Awaria', [
    // dawna kategoria "Awaria"
    'awari', 'błąd', 'blad', 'nie działa', 'zatrzymał', 'zatrzymała',
    'zepsuty', 'zepsuta', 'zwarcie', 'przegrzewa', 'przegrzała', 'zacięcie', 'zacięty',
    ...COLLOQUIAL_FAILURE_WORDS,
    // dawna kategoria "Jakość"
    'wada', 'skaza', 'defekt', 'przebarwienie', 'odrzut', 'niezgodność', 'reklamacja',
    'nie przeszła kontroli', 'nie przeszło kontroli', 'jakość', 'jakości',
    // dawna kategoria "Serwis"
    'wyciek oleju', 'wyciek smaru', 'przegląd', 'konserwacja', 'naprawa', 'część zamienna',
    'serwis', 'serwisant', 'wyciek', 'cieknie',
  ]],
  ['Magazyn', [
    // dawna kategoria "Braki"
    'brak', 'braki', 'puste', 'pusty', 'zabrakło', 'niedobór', 'kończy się', 'skończył się',
    'magazyn', 'magazynie', 'stan magazynowy', 'zapas', 'zapasy',
  ]],
]

export function detectIncidentCategory(text: string): IncidentCategory {
  const t = normalize(text)
  for (const [category, keywords] of CATEGORY_KEYWORDS) {
    if (keywords.some(k => t.includes(k))) return category
  }
  return 'Awaria'
}

function extractErrorCode(t: string): string | null {
  const m = t.match(/(?:błąd|blad|kod|error)\s*[:\-]?\s*([a-z]{0,2}\d{1,4})\b/i)
  if (m) return m[1].toUpperCase()
  const m2 = t.match(/\b([a-z]\d{2,3})\b/i)
  return m2 ? m2[1].toUpperCase() : null
}

// Rdzenie + formy 2 os. l.mn. ("wezwijcie", "zróbcie") — brygadzista zwraca się
// często do całej zmiany/ekipy, nie do jednej osoby, a same formy podstawowe
// ("wezwij") tego nie łapały.
const COMMAND_VERBS = [
  'wyłącz', 'wyłączyć', 'wyłączcie', 'włącz', 'włączyć', 'włączcie',
  'zamknij', 'zamknąć', 'zamknijcie', 'odgrodź', 'odgrodzić', 'odgrodźcie',
  'oznacz', 'oznaczyć', 'oznaczcie', 'zabezpiecz', 'zabezpieczyć', 'zabezpieczcie',
  'wyczyść', 'wyczyścić', 'wyczyśćcie', 'posprzątaj', 'posprzątać', 'posprzątajcie',
  'przydziel', 'przydzielić', 'przydzielcie', 'napraw', 'naprawić', 'naprawcie',
  'wezwij', 'wezwać', 'wezwijcie', 'dajcie', 'dać', 'daj',
  'poinformuj', 'poinformować', 'poinformujcie', 'zawiadom', 'zawiadomić', 'zawiadomcie',
  'wydaj', 'wydajcie', 'zawołaj', 'zawołać', 'zawołajcie',
  'zgłoście', 'zgłosić', 'sprawdźcie', 'sprawdzić', 'sprawdź',
]

function normalize(t: string): string {
  return t.toLowerCase().replace(/[.,!?;:""''()\-]/g, ' ').replace(/\s+/g, ' ').trim()
}

function toNumber(w: string): string | null {
  const c = w.trim().toLowerCase()
  return NUMBERS[c] ?? null
}

// Wszystkie słowa kluczowe kategorii (BHP/Awaria/Jakość/Braki/Serwis) muszą też
// uruchamiać samo wykrycie "to jest zgłoszenie" — INCIDENT_WORDS był węższą,
// osobną listą i przepuszczał np. "naprawa"/"serwis"/"wada"/"reklamacja", mimo że
// te same słowa poprawnie klasyfikują kategorię, gdyby tekst w ogóle tu dotarł.
// Realny przykład z produkcji, który wcześniej NIE był wykrywany jako incydent:
// "Hala B maszyna 43 naprawa, serwis pod potrzebny pilnie."
const ALL_CATEGORY_WORDS = CATEGORY_KEYWORDS.flatMap(([, words]) => words)

export function isIncident(text: string): boolean {
  const t = normalize(text)
  return INCIDENT_WORDS.some(w => t.includes(w)) || ALL_CATEGORY_WORDS.some(w => t.includes(w))
}

export function parseIncident(text: string, existing: IncidentForm | null = null): IncidentForm {
  checkpoint('PARSER', 'parseIncident input', { text })
  const base = existing ? { ...existing } : {
    lokalizacja: '', obiekt: '', typ: '', waznosc: '', opis: '', polecenia: '', ppe: '', kategoria: '',
  }
  const t = normalize(text)

  base.kategoria = detectIncidentCategory(text)

  // lokalizacja: (w/na/przy) alei/hali/linii/strefie/budynku X — przyimek jest
  // opcjonalny, bo zestresowany brygadzista często zaczyna zdanie wprost od
  // nazwy strefy ("Hala 3, prasa 5 stanęła!"), bez "w"/"na"/"przy" przed nią.
  // Pozycja numeru w lokalizacji (locNumberRange) — potrzebna niżej, żeby ten SAM
  // numer nie doklejał się jednocześnie do lokalizacji i do obiektu (np. "ładowarka
  // w linii 3" dawało błędnie "ładowarka 3" zamiast "ładowarka").
  const locRegex = /(?:(?:w|na|przy)\s+)?(?:alei|aleja|hali|hala|linii|linia|strefie|strefa|budynku|budynek)\s+(?:produkcyjnej\s+|montażowej\s+|magazynowej\s+)?(\d+|[a-ząćęłńóśźż]+)\b/
  const locMatch = locRegex.exec(t)
  let locNumberRange: [number, number] | null = null
  if (locMatch) {
    let loc = locMatch[1].toLowerCase().replace(/ej$/, 'a')
    const n = toNumber(loc)
    const raw = locMatch[0].toLowerCase()
    const type = raw.includes('ale') ? 'aleja' : raw.includes('lini') ? 'linia' : raw.includes('stref') ? 'strefa' : raw.includes('budyn') ? 'budynek' : 'hala'
    base.lokalizacja = `${type} ${n ?? loc}`
    if (n) locNumberRange = groupRange(locMatch, locMatch[1])
  }

  // Jeśli "linia"/"linii" zostało już zużyte jako LOKALIZACJA, nie kwalifikuje się
  // też jako OBIEKT tego samego zdarzenia — inaczej "linia 3" trafiało identycznie
  // w obydwa pola i gubiło rzeczywisty obiekt (np. "hala B"), gdy oba wystąpiły razem.
  const locUsedLinia = !!base.lokalizacja && /^linia\b/.test(base.lokalizacja)
  const objectCandidates = locUsedLinia ? KNOWN_OBJECTS.filter(o => o !== 'linia' && o !== 'linii') : KNOWN_OBJECTS

  // Do 2 dodatkowych słów między nazwą obiektu a numerem — dopuszcza markę/model
  // pomiędzy nimi (np. "zgrzewarka bizon 17"), a nie tylko "numer/nr".
  const numberRegexFor = (o: string) => {
    const safe = o.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    return new RegExp(safe + '(?:\\s+(?:numer|nr)\\b)?(?:\\s+[a-ząćęłńóśźż]+){0,2}?\\s+(?:produkcyjnej\\s+|montażowej\\s+|magazynowej\\s+)?(\\d+|' + Object.keys(NUMBERS).join('|') + ')\\b')
  }

  // Numer, który już trafił do lokalizacji (locNumberRange), nie może też zostać
  // przypisany do obiektu — inaczej "ładowarka w linii 3" dawało "ładowarka 3",
  // mimo że ten "3" należy do linii, nie do ładowarki.
  function matchObjectNumber(o: string): { num: string | null } {
    const m = numberRegexFor(o).exec(t)
    if (!m) return { num: null }
    const numRange = groupRange(m, m[1])
    if (locNumberRange && numRange && numRange[0] === locNumberRange[0] && numRange[1] === locNumberRange[1]) {
      return { num: null }
    }
    return { num: NUMBERS[m[1].toLowerCase()] ?? m[1] }
  }

  // Zbierz WSZYSTKIE trafienia (nie tylko pierwsze) — jeśli w wypowiedzi jest i słowo
  // ogólne ("maszyna"), i konkretna nazwa ("zgrzewarka"), wygrywa ta konkretna. Gdy
  // jest kilka konkretnych nazw naraz, priorytet ma ta, przy której faktycznie pada
  // numer (np. "zgrzewarka bizon 17" wygrywa z samym "podajnikiem" bez numeru).
  const foundObjects = objectCandidates.filter(o => {
    const safe = o.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    return new RegExp('(?:^|[\\s.,;!?:-])' + safe + '(?:[\\s.,;!?:-]|$)').test(t)
  })
  const specific = foundObjects.filter(o => !GENERIC_OBJECT_WORDS.has(o))
  const specificWithNumber = specific.find(o => matchObjectNumber(o).num !== null)
  const foundObject = specificWithNumber || specific[0] || foundObjects[0]
  if (foundObject) {
    const num = matchObjectNumber(foundObject).num
    const objectName = OBJECT_BASE[foundObject] || foundObject
    base.obiekt = objectName.toUpperCase() === 'cnc' ? (num ? `CNC ${num}` : 'CNC') : (num ? `${objectName} ${num}` : objectName)
    // Jeśli lokalizacja (bardziej rozbudowany regex) wykryła numer, którego obiekt nie złapał — uzupełnij
    if (!num && base.lokalizacja && new RegExp(`\\b${objectName}\\b`).test(base.lokalizacja) && /\d/.test(base.lokalizacja)) {
      base.obiekt = base.lokalizacja
    }
  } else {
    const objMatch = t.match(/(?:przy maszynie|przy sprzęcie|przy urządzeniu|urządzenie|obiekt|maszyna)\s+(?:numer\s+|nr\s+)?(\S+(?:\s+\d+)?)/)
    if (objMatch) base.obiekt = objMatch[1].replace(/[.,;]/g, '')
  }

  // typ
  const errorCode = extractErrorCode(t)
  if (errorCode) base.typ = `błąd ${errorCode}`
  else if (t.includes('kwas') && (t.includes('plama') || t.includes('wyciek'))) base.typ = 'plama kwasu'
  else if (t.includes('skaza')) {
    const m = t.match(/skaza\s+(\S+)/)
    base.typ = m ? `skaza ${m[1]}` : 'skaza'
  }
  else if (t.includes('puste') || t.includes('zabrakło') || (t.includes('brak') && !t.includes('brakiem'))) {
    const m = t.match(/(?:puste|zabrakło|brak)\s+(\S+)/)
    base.typ = m ? `${t.includes('puste') ? 'puste' : 'brak'} ${m[1]}` : (t.includes('puste') ? 'puste' : 'brak materiału')
  }
  else if (t.includes('wada') || t.includes('niezgodność') || t.includes('reklamacj')) base.typ = 'wada jakościowa'
  else if (/(wyciek|wyleciek)\s+oleju/.test(t) || ((t.includes('wyciek') || t.includes('wyleciek')) && t.includes('olej'))) base.typ = 'wyciek oleju'
  else if (t.includes('poślizg') || t.includes('poślizgnął') || t.includes('ślisko')) base.typ = 'zagrożenie poślizgiem'
  else if (t.includes('awari')) base.typ = 'awaria'
  else if (t.includes('zepsuty') || t.includes('zepsuta') || t.includes('zepsuł') || t.includes('zepsuła') || t.includes('nie działa') || t.includes('przestał działać') || t.includes('przestała działać')) base.typ = 'awaria'
  else if (COLLOQUIAL_FAILURE_WORDS.some(w => t.includes(w))) base.typ = 'awaria'
  else if (t.includes('wyciek') || t.includes('wyleciek')) base.typ = 'wyciek'
  else if (t.includes('upadek') || t.includes('upadł')) base.typ = 'upadek'
  else if (t.includes('uszkodzenie')) base.typ = 'uszkodzenie'
  else if (t.includes('pęknięcie') || t.includes('pęknięty')) base.typ = 'pęknięcie'
  else if (t.includes('wycieka') || t.includes('cieknie')) base.typ = 'nieszczelność'

  // ważność
  if (t.includes('pilne') || t.includes('pilny') || t.includes('natychmiast') || t.includes('krytyczna') || t.includes('krytyczne')) base.waznosc = 'wysoka'
  else if (t.includes('średnie') || t.includes('umiarkowane')) base.waznosc = 'średnia'
  else if (t.includes('niskie') || t.includes('niewielkie')) base.waznosc = 'niska'

  const stopVerbs = ['poleceni', 'polecenie', 'ppe', 'środki ochrony', 'ochrona', ...COMMAND_VERBS].map(v => v.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')

  // PPE
  const ppeMatch = t.match(new RegExp('(?:ppe|środki\\s+ochrony|ochrona)\\s+(.+?)(?=\\s(?:' + stopVerbs + ')\\b|$)'))
  if (ppeMatch) base.ppe = ppeMatch[1].trim()

  // polecenia: wezwij/wydaj/zamknij ...
  const commands: string[] = []
  const allVerbs = COMMAND_VERBS.map(v => v.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')
  const verbRegex = new RegExp(`(?:^|\\s)(${allVerbs})\\s+(.*?)(?=\\s+(?:${allVerbs})\\s|$)`, 'g')
  const matches = Array.from(t.matchAll(verbRegex))
  for (const m of matches) {
    const rest = m[2].trim()
    if (rest) commands.push(`${m[1]} ${rest}`)
  }
  // "proszę o X" / "potrzebny X (natychmiast/pilnie)" — częsta konstrukcja bez
  // czasownika z listy COMMAND_VERBS (np. "proszę o serwis natychmiast"), którą
  // powyższy wzorzec w ogóle nie łapie, bo nie ma w niej żadnego z tych czasowników.
  const pleaseMatch = t.match(/proszę\s+o\s+(.+?)(?=\s(?:i\s+proszę|proszę)\b|$)/)
  if (pleaseMatch) {
    const rest = pleaseMatch[1].trim()
    if (rest) commands.push(`proszę o ${rest}`)
  }
  const neededMatch = t.match(/(?:potrzebn[ay])\s+(.+?)(?=$)/)
  if (neededMatch && !pleaseMatch) {
    const rest = neededMatch[1].trim()
    if (rest) commands.push(`potrzebny ${rest}`)
  }

  if (commands.length) {
    const newCmd = commands.join('\n')
    base.polecenia = base.polecenia ? `${base.polecenia}\n${newCmd}` : newCmd
  }

  // opis: treść między słowem kluczowym a polami kończącymi
  const opisMatch = t.match(new RegExp('(?:opis(?:\\s+zdarzenia)?|opisie)\\s+(.+?)(?=\\s(?:' + stopVerbs + ')\\b|$)'))
  base.opis = opisMatch ? opisMatch[1].trim() : (base.lokalizacja || base.obiekt || base.typ ? '' : text.trim())

  if (!base.opis && text.trim()) {
    // opis to pozostałość po usunięciu wyciągniętych pól
    base.opis = text.trim()
  }

  checkpoint('PARSER', 'parseIncident output', { text, form: base, missing: validateIncident(base) })
  return base
}

export function validateIncident(form: IncidentForm): (keyof IncidentForm)[] {
  const required: (keyof Omit<IncidentForm, 'polecenia_arr'>)[] = ['lokalizacja', 'obiekt', 'typ']
  return required.filter(k => !form[k]?.toString().trim()) as (keyof IncidentForm)[]
}

// Pozycja przechwyconej grupy regexu w pełnym tekście — używane, żeby ten SAM
// numer nie doklejał się jednocześnie do lokalizacji i do obiektu. Bez flagi "d"
// (wymaga ES2022, projekt celuje w ES2020) liczymy pozycję ręcznie: grupa numeru
// jest zawsze ostatnim elementem dopasowania w obu wzorcach niżej.
function groupRange(m: RegExpExecArray, group: string): [number, number] | null {
  const offset = m[0].lastIndexOf(group)
  if (offset === -1) return null
  const start = m.index + offset
  return [start, start + group.length]
}

function removeNumberJunk(t: string): string {
  return t.replace(/\b(?:numer|nr|nr\.|numerek|sztuka|szt\.|jednostka)\b/gi, '').replace(/\s+/g, ' ').trim()
}

function extractShortOpis(opis: string, excludeWords: string[] = []): string {
  const exclude = new Set(excludeWords.map(w => normalize(w)).join(' ').split(/\s+/).filter(Boolean))
  const words = normalize(opis).split(/\s+/).filter(w => Boolean(w) && !exclude.has(w)).slice(0, 4)
  return words.join(' ')
}

// Wzór: [KATEGORIA] - [Maszyna/Strefa] [Co się stało]
// Przykłady: "Awaria - Linia 2 błąd E40", "BHP - Wanna 4 plama kwasu"
export function formatIncidentTitle(form: IncidentForm): { bold: string, rest: string } {
  const bold = (form.kategoria?.trim() as string) || detectIncidentCategory(form.opis || form.typ || '')

  const place = form.obiekt?.trim() ? removeNumberJunk(form.obiekt).length > 0 ? form.obiekt.trim() : '' : (form.lokalizacja?.trim() || '')
  const rawEvent = form.typ?.trim() || (form.opis?.trim() ? extractShortOpis(form.opis, [place]) : '')
  // Unikaj powtórzenia nazwy kategorii w opisie zdarzenia (np. "Awaria - X awaria")
  const event = rawEvent.toLowerCase() === bold.toLowerCase() ? '' : rawEvent

  let restText = [place, event].filter(Boolean).join(' ').trim()
  if (restText.length > 40) restText = restText.slice(0, 40).trim() + '…'
  const rest = restText ? ` - ${restText}` : ''

  return { bold, rest }
}

const FIELD_ALIASES: Record<string, (keyof IncidentForm)[]> = {
  lokalizacja: ['lokalizacja'], lokalizację: ['lokalizacja'], miejsce: ['lokalizacja'],
  obiekt: ['obiekt'], maszynę: ['obiekt'], maszyny: ['obiekt'], sprzęt: ['obiekt'], urządzenie: ['obiekt'],
  typ: ['typ'], rodzaj: ['typ'], zdarzenie: ['typ'],
  ważność: ['waznosc'], waznosc: ['waznosc'], pilność: ['waznosc'],
  opis: ['opis'], opisie: ['opis'], 'opis zdarzenia': ['opis'],
  polecenia: ['polecenia'], polecenie: ['polecenia'], zlecenia: ['polecenia'], ustalenia: ['polecenia'],
  ppe: ['ppe'], 'środki ochrony': ['ppe'], rękawice: ['ppe'], gogle: ['ppe'],
}

function findField(word: string): keyof IncidentForm | null {
  const w = normalize(word)
  for (const [alias, fields] of Object.entries(FIELD_ALIASES)) {
    if (w.includes(alias) || alias.includes(w)) return fields[0]
  }
  return null
}

export function updateIncident(form: IncidentForm, text: string): IncidentForm {
  const t = normalize(text)
  const result = { ...form }

  // komendy: wykasz / usuń <pole>
  const clearMatch = t.match(/(?:wykasz|usuń|wyczyść)\s+(\S+(?:\s+\S+)?)/)
  if (clearMatch) {
    const field = findField(clearMatch[1])
    if (field) {
      result[field] = '' as any
      return result
    }
  }

  // komendy: zmień / popraw <pole> (na) <wartość>
  const changeMatch = t.match(/(?:zmień|popraw)\s+(\S+(?:\s+\S+)?)(?:\s+na\s+)?(.+?)(?:\.|$)/)
  if (changeMatch) {
    const field = findField(changeMatch[1])
    const value = changeMatch[2].trim()
    if (field && value) {
      result[field] = value as any
      return result
    }
  }

  // komendy: dodaj do <pole> <wartość>
  const addMatch = t.match(/(?:dodaj do|dopisz do)\s+(\S+(?:\s+\S+)?)\s+(.+?)(?:\.|$)/)
  if (addMatch) {
    const field = findField(addMatch[1])
    const value = addMatch[2].trim()
    if (field && value) {
      const cur = (result[field] ?? '') as string
      result[field] = (cur ? `${cur}\n${value}` : value) as any
      return result
    }
  }

  // w przeciwnym razie automatyczne wyciągnięcie z tekstu
  const extracted = parseIncident(text, form)
  return extracted
}
